<template>
    <div class="am-dashboard">
        <div class="dashboard-header">
            <div class="header-content">
                <h1>Tableau de bord Manager</h1>
                <p class="subtitle">Bienvenue, {{ userName }}</p>
            </div>
            <!-- Date Filter -->
            <div class="date-filter">
                <div class="filter-group">
                    <label>Période</label>
                    <select v-model="dateType" @change="onDateTypeChange" class="date-select">
                        <option value="today">Aujourd'hui</option>
                        <option value="yesterday">Hier</option>
                        <option value="thisweek">Cette semaine</option>
                        <option value="thismonth">Ce mois</option>
                        <option value="last30days">30 derniers jours</option>
                        <option value="custom">Personnalisé</option>
                    </select>
                </div>
                <div v-if="dateType === 'custom'" class="custom-dates">
                    <div class="filter-group">
                        <label>Du</label>
                        <input type="date" v-model="dateFrom" class="date-input" />
                    </div>
                    <div class="filter-group">
                        <label>Au</label>
                        <input type="date" v-model="dateTo" class="date-input" />
                    </div>
                </div>
                <button @click="applyDateFilter" class="apply-btn" :disabled="loadingSellers">
                    <i class="material-icons">filter_list</i>
                    Filtrer
                </button>
            </div>
        </div>

        <!-- Stats Cards -->
        <div class="stats-grid">
            <div class="stat-card">
                <div class="stat-icon sellers">
                    <i class="material-icons">people</i>
                </div>
                <div class="stat-content">
                    <span class="stat-value">{{ stats.totalSellers }}</span>
                    <span class="stat-label">Vendeurs assignés</span>
                </div>
            </div>

            <div class="stat-card">
                <div class="stat-icon messages">
                    <i class="material-icons">chat</i>
                </div>
                <div class="stat-content">
                    <span class="stat-value">{{ stats.activeConversations }}</span>
                    <span class="stat-label">Conversations actives</span>
                </div>
            </div>

            <div class="stat-card">
                <div class="stat-icon unread">
                    <i class="material-icons">markunread</i>
                </div>
                <div class="stat-content">
                    <span class="stat-value">{{ stats.unreadMessages }}</span>
                    <span class="stat-label">Messages non lus</span>
                </div>
            </div>

            <div class="stat-card" :class="{ 'alert': stats.pendingAlerts > 0 }">
                <div class="stat-icon alerts">
                    <i class="material-icons">warning</i>
                </div>
                <div class="stat-content">
                    <span class="stat-value">{{ stats.pendingAlerts }}</span>
                    <span class="stat-label">Alertes en attente</span>
                </div>
            </div>
        </div>

        <!-- Recent Activity -->
        <div class="dashboard-sections">
            <!-- Recent Conversations -->
            <div class="section-card">
                <div class="section-header">
                    <h2>Conversations récentes</h2>
                    <router-link to="/account-manager/chat" class="view-all">
                        Voir tout <i class="material-icons">arrow_forward</i>
                    </router-link>
                </div>
                <div class="section-content">
                    <div v-if="loadingConversations" class="loading">
                        <div class="spinner"></div>
                    </div>
                    <div v-else-if="recentConversations.length === 0" class="empty-state">
                        <i class="material-icons">chat_bubble_outline</i>
                        <p>Aucune conversation récente</p>
                    </div>
                    <div v-else class="conversation-list">
                        <div
                            v-for="conv in recentConversations"
                            :key="conv._id"
                            class="conversation-item"
                            @click="goToConversation(conv._id)"
                        >
                            <div class="conv-avatar">
                                <span>{{ getInitial(conv.seller) }}</span>
                            </div>
                            <div class="conv-info">
                                <span class="conv-name">{{ getSellerName(conv.seller) }}</span>
                                <span class="conv-preview">{{ getLastMessagePreview(conv) }}</span>
                            </div>
                            <div class="conv-meta">
                                <span class="conv-time">{{ formatTime(conv.lastMessage && conv.lastMessage.sentAt) }}</span>
                                <span v-if="conv.unreadByAM > 0" class="unread-badge">{{ conv.unreadByAM }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Assigned Sellers -->
            <div class="section-card">
                <div class="section-header">
                    <h2>Mes vendeurs</h2>
                    <div class="header-actions">
                        <span class="date-badge" v-if="currentDateLabel">{{ currentDateLabel }}</span>
                        <router-link to="/account-manager/sellers" class="view-all">
                            Voir tout <i class="material-icons">arrow_forward</i>
                        </router-link>
                    </div>
                </div>
                <div class="section-content">
                    <div v-if="loadingSellers" class="loading">
                        <div class="spinner"></div>
                    </div>
                    <div v-else-if="assignedSellers.length === 0" class="empty-state">
                        <i class="material-icons">person_off</i>
                        <p>Aucun vendeur assigné</p>
                    </div>
                    <div v-else class="seller-list">
                        <div
                            v-for="seller in assignedSellers.slice(0, 5)"
                            :key="seller._id"
                            class="seller-item"
                            @click="goToSellerStats(seller._id)"
                        >
                            <div class="seller-avatar">
                                <span>{{ getInitial(seller) }}</span>
                            </div>
                            <div class="seller-info">
                                <span class="seller-name">{{ seller.fullName || seller.email }}</span>
                                <span class="seller-country">{{ formatCountries(seller.countries) }}</span>
                            </div>
                            <div class="seller-stats">
                                <span class="orders-count" :class="{ 'has-orders': seller.ordersCount > 0 }">
                                    {{ seller.ordersCount || 0 }} commandes
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Top Sellers Ranking -->
        <div class="ranking-section">
            <div class="section-header">
                <div class="section-title-group">
                    <h2>Classement des Top Sellers</h2>
                    <span class="date-badge" v-if="currentDateLabel">{{ currentDateLabel }}</span>
                </div>
                <button @click="loadTopSellers" class="refresh-btn" :disabled="loadingTopSellers">
                    <i class="material-icons" :class="{ 'spinning': loadingTopSellers }">refresh</i>
                </button>
            </div>
            <div class="section-content">
                <div v-if="loadingTopSellers" class="loading">
                    <div class="spinner"></div>
                </div>
                <div v-else-if="topSellers.length === 0" class="empty-state">
                    <i class="material-icons">leaderboard</i>
                    <p>Aucun vendeur avec des données de performance</p>
                </div>
                <div v-else class="ranking-table-wrapper">
                    <table class="ranking-table">
                        <thead>
                            <tr>
                                <th class="rank-col">Rang</th>
                                <th class="seller-col">Vendeur</th>
                                <th class="revenue-col">Chiffre d'affaires</th>
                                <th class="orders-col">Commandes</th>
                                <th class="rate-col">Taux Confirmation</th>
                                <th class="rate-col">Taux Livraison</th>
                                <th class="rate-col">Global Cmd/Livré</th>
                                <th class="evolution-col">Évolution</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="seller in topSellers" :key="seller._id" @click="goToSellerStats(seller._id)" class="ranking-row">
                                <td class="rank-col">
                                    <span class="rank-badge" :class="getRankClass(seller.rank)">
                                        <i v-if="seller.rank <= 3" class="material-icons">{{ getRankIcon(seller.rank) }}</i>
                                        <span v-else>{{ seller.rank }}</span>
                                    </span>
                                </td>
                                <td class="seller-col">
                                    <div class="seller-cell">
                                        <div class="seller-avatar-small">
                                            <span>{{ getInitial(seller) }}</span>
                                        </div>
                                        <div class="seller-details">
                                            <span class="seller-name">{{ seller.fullName || seller.email }}</span>
                                            <span class="seller-company" v-if="seller.company">{{ seller.company }}</span>
                                        </div>
                                    </div>
                                </td>
                                <td class="revenue-col">
                                    <span class="revenue-value">{{ formatCurrency(seller.revenue) }}</span>
                                </td>
                                <td class="orders-col">
                                    <span class="orders-value">{{ seller.ordersCount }}</span>
                                </td>
                                <td class="rate-col">
                                    <div class="rate-cell">
                                        <div class="rate-bar">
                                            <div class="rate-fill confirmation" :style="{ width: seller.confirmationRate + '%' }"></div>
                                        </div>
                                        <span class="rate-value">{{ seller.confirmationRate }}%</span>
                                    </div>
                                </td>
                                <td class="rate-col">
                                    <div class="rate-cell">
                                        <div class="rate-bar">
                                            <div class="rate-fill delivery" :style="{ width: seller.deliveryRate + '%' }"></div>
                                        </div>
                                        <span class="rate-value">{{ seller.deliveryRate }}%</span>
                                    </div>
                                </td>
                                <td class="rate-col">
                                    <div class="rate-cell">
                                        <div class="rate-bar">
                                            <div class="rate-fill global" :style="{ width: seller.globalOrderDeliveredRate + '%' }"></div>
                                        </div>
                                        <span class="rate-value">{{ seller.globalOrderDeliveredRate }}%</span>
                                    </div>
                                </td>
                                <td class="evolution-col">
                                    <div class="evolution-indicators">
                                        <span class="evolution-badge" :class="getEvolutionClass(seller.evolution.revenue)" :title="'Revenus: ' + formatEvolution(seller.evolution.revenue)">
                                            <i class="material-icons">{{ getEvolutionIcon(seller.evolution.revenue) }}</i>
                                            {{ Math.abs(seller.evolution.revenue) }}%
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Quick Actions -->
        <div class="quick-actions">
            <h2>Actions rapides</h2>
            <div class="actions-grid">
                <router-link to="/account-manager/chat" class="action-btn">
                    <i class="material-icons">chat</i>
                    <span>Ouvrir le chat</span>
                </router-link>
                <router-link to="/account-manager/sellers" class="action-btn">
                    <i class="material-icons">people</i>
                    <span>Voir mes vendeurs</span>
                </router-link>
                <router-link to="/account-manager/alerts" class="action-btn" v-if="isAdmin">
                    <i class="material-icons">notifications</i>
                    <span>Gérer les alertes</span>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AccountManagerDashboard',
    data() {
        return {
            stats: {
                totalSellers: 0,
                activeConversations: 0,
                unreadMessages: 0,
                pendingAlerts: 0
            },
            recentConversations: [],
            assignedSellers: [],
            topSellers: [],
            loadingConversations: false,
            loadingSellers: false,
            loadingTopSellers: false,
            // Date filter
            dateType: 'last30days',
            dateFrom: '',
            dateTo: ''
        };
    },
    computed: {
        userName() {
            const user = this.$store.state.user;
            return (user && user.fullName) || (user && user.firstName) || 'Account Manager';
        },
        isAdmin() {
            const user = this.$store.state.user;
            return user && user.type === 'Admin';
        },
        currentDateLabel() {
            const labels = {
                'today': "Aujourd'hui",
                'yesterday': 'Hier',
                'thisweek': 'Cette semaine',
                'thismonth': 'Ce mois',
                'last30days': '30 derniers jours',
                'custom': this.dateFrom && this.dateTo ? `${this.formatDate(this.dateFrom)} - ${this.formatDate(this.dateTo)}` : ''
            };
            return labels[this.dateType] || '';
        }
    },
    async mounted() {
        this.initDefaultDates();
        await this.loadDashboardData();
    },
    methods: {
        initDefaultDates() {
            const today = new Date();
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(today.getDate() - 30);
            this.dateTo = today.toISOString().split('T')[0];
            this.dateFrom = thirtyDaysAgo.toISOString().split('T')[0];
        },
        onDateTypeChange() {
            if (this.dateType !== 'custom') {
                this.applyDateFilter();
            }
        },
        async applyDateFilter() {
            await Promise.all([
                this.loadSellersWithDateFilter(),
                this.loadTopSellers()
            ]);
        },
        async loadDashboardData() {
            try {
                // Load dashboard stats
                const dashboardRes = await this.$server.getDashboard('accountManager');
                if (dashboardRes && dashboardRes.content) {
                    const data = dashboardRes.content;
                    this.stats = {
                        totalSellers: (data.sellers && data.sellers.total) || 0,
                        activeConversations: (data.conversations && data.conversations.total) || 0,
                        unreadMessages: (data.conversations && data.conversations.unreadMessages) || 0,
                        pendingAlerts: (data.conversations && data.conversations.activeAlerts) || 0
                    };
                }

                // Load recent conversations
                this.loadingConversations = true;
                const convsRes = await this.$server.getMyConversations('conversations', { limit: 5 });
                if (convsRes && convsRes.content && convsRes.content.results) {
                    this.recentConversations = convsRes.content.results;
                }

                // Load assigned sellers with date filter
                await this.loadSellersWithDateFilter();

                // Load top sellers ranking
                await this.loadTopSellers();
            } catch (error) {
                console.error('Failed to load dashboard data:', error);
            } finally {
                this.loadingConversations = false;
            }
        },
        async loadSellersWithDateFilter() {
            try {
                this.loadingSellers = true;

                const params = { limit: 10 };

                // Add date filter parameters
                if (this.dateType === 'custom' && this.dateFrom && this.dateTo) {
                    params.dateFrom = this.dateFrom;
                    params.dateTo = this.dateTo;
                } else {
                    params.dateType = this.dateType;
                }

                const sellersRes = await this.$server.getAssignedSellers('accountManager', params);
                if (sellersRes && sellersRes.content && sellersRes.content.results) {
                    this.assignedSellers = sellersRes.content.results;
                }
            } catch (error) {
                console.error('Failed to load sellers:', error);
            } finally {
                this.loadingSellers = false;
            }
        },
        async loadTopSellers() {
            try {
                this.loadingTopSellers = true;

                const params = { limit: 10 };

                // Add date filter parameters
                if (this.dateType === 'custom' && this.dateFrom && this.dateTo) {
                    params.dateFrom = this.dateFrom;
                    params.dateTo = this.dateTo;
                } else {
                    params.dateType = this.dateType;
                }

                const response = await this.$server.getTopSellers('accountManager', params);
                if (response && response.content && response.content.results) {
                    this.topSellers = response.content.results;
                }
            } catch (error) {
                console.error('Failed to load top sellers:', error);
            } finally {
                this.loadingTopSellers = false;
            }
        },
        formatCurrency(amount) {
            if (!amount) return '0 MAD';
            return new Intl.NumberFormat('fr-FR', {
                style: 'decimal',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(amount) + ' MAD';
        },
        getRankClass(rank) {
            if (rank === 1) return 'gold';
            if (rank === 2) return 'silver';
            if (rank === 3) return 'bronze';
            return '';
        },
        getRankIcon(rank) {
            if (rank === 1) return 'emoji_events';
            if (rank === 2) return 'military_tech';
            if (rank === 3) return 'workspace_premium';
            return '';
        },
        getEvolutionClass(value) {
            if (value > 0) return 'positive';
            if (value < 0) return 'negative';
            return 'neutral';
        },
        getEvolutionIcon(value) {
            if (value > 0) return 'trending_up';
            if (value < 0) return 'trending_down';
            return 'trending_flat';
        },
        formatEvolution(value) {
            if (value > 0) return '+' + value + '%';
            return value + '%';
        },
        getInitial(user) {
            if (!user) return '?';
            const name = user.fullName || user.firstName || user.email || '';
            return name.charAt(0).toUpperCase();
        },
        getSellerName(seller) {
            if (!seller) return 'Vendeur';
            return seller.fullName || `${seller.firstName || ''} ${seller.lastName || ''}`.trim() || seller.email;
        },
        getLastMessagePreview(conversation) {
            const msg = conversation && conversation.lastMessage;
            if (!msg) return 'Aucun message';
            if (msg.type === 'image') return 'Image';
            if (msg.type === 'file') return 'Fichier';
            const content = msg.content || '';
            return content.length > 40 ? content.substring(0, 40) + '...' : content;
        },
        formatTime(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            const now = new Date();
            const diffMs = now - date;
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffDays === 0) {
                return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
            } else if (diffDays === 1) {
                return 'Hier';
            } else if (diffDays < 7) {
                return date.toLocaleDateString('fr-FR', { weekday: 'short' });
            } else {
                return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
        },
        formatCountries(countries) {
            if (!countries || countries.length === 0) return '-';
            return countries.join(', ');
        },
        goToConversation(conversationId) {
            this.$router.push(`/account-manager/chat/${conversationId}`);
        },
        goToSellerStats(sellerId) {
            this.$router.push(`/account-manager/sellers/${sellerId}`);
        }
    }
};
</script>

<style scoped>
.am-dashboard {
    padding: 24px;
}

.dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 32px;
}

.header-content h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: #111827;
}

.subtitle {
    margin: 8px 0 0;
    color: #6b7280;
    font-size: 16px;
}

/* Date Filter Styles */
.date-filter {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    background: #ffffff;
    padding: 16px;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.filter-group label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
}

.date-select,
.date-input {
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    color: #111827;
    background: #fff;
    min-width: 150px;
}

.date-select:focus,
.date-input:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.2);
}

.custom-dates {
    display: flex;
    gap: 12px;
}

.apply-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: #059669;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
}

.apply-btn:hover:not(:disabled) {
    background: #047857;
}

.apply-btn:disabled {
    background: #9ca3af;
    cursor: not-allowed;
}

.apply-btn i {
    font-size: 18px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    margin-bottom: 32px;
}

.stat-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-card.alert {
    border-left: 4px solid #ef4444;
}

.stat-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.stat-icon i {
    font-size: 28px;
    color: #ffffff;
}

.stat-icon.sellers {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.stat-icon.messages {
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
}

.stat-icon.unread {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.stat-icon.alerts {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.stat-content {
    display: flex;
    flex-direction: column;
}

.stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
}

.stat-label {
    font-size: 14px;
    color: #6b7280;
}

.dashboard-sections {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 24px;
    margin-bottom: 32px;
}

.section-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
}

.section-header h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.date-badge {
    font-size: 12px;
    padding: 4px 8px;
    background: #ecfdf5;
    color: #059669;
    border-radius: 4px;
    font-weight: 500;
}

.view-all {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #059669;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
}

.view-all:hover {
    text-decoration: underline;
}

.view-all i {
    font-size: 18px;
}

.section-content {
    padding: 8px 0;
    min-height: 200px;
}

.loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 150px;
}

.spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: #059669;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 150px;
    color: #9ca3af;
}

.empty-state i {
    font-size: 40px;
    margin-bottom: 8px;
    opacity: 0.5;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
}

.conversation-item,
.seller-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.conversation-item:hover,
.seller-item:hover {
    background-color: #f9fafb;
}

.conv-avatar,
.seller-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 16px;
    flex-shrink: 0;
}

.conv-info,
.seller-info {
    flex: 1;
    min-width: 0;
}

.conv-name,
.seller-name {
    display: block;
    font-weight: 600;
    color: #111827;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.conv-preview {
    display: block;
    font-size: 13px;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.seller-country {
    display: block;
    font-size: 13px;
    color: #6b7280;
}

.conv-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
}

.conv-time {
    font-size: 12px;
    color: #9ca3af;
}

.unread-badge {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    background-color: #059669;
    color: white;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
}

.seller-stats {
    text-align: right;
}

.orders-count {
    font-size: 13px;
    color: #6b7280;
    padding: 4px 8px;
    background: #f3f4f6;
    border-radius: 4px;
}

.orders-count.has-orders {
    background: #ecfdf5;
    color: #059669;
    font-weight: 500;
}

.quick-actions {
    background: #ffffff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.quick-actions h2 {
    margin: 0 0 16px;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.actions-grid {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.action-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background-color: #f3f4f6;
    border-radius: 8px;
    color: #374151;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s;
}

.action-btn:hover {
    background-color: #e5e7eb;
    color: #111827;
}

.action-btn i {
    font-size: 20px;
    color: #059669;
}

/* Top Sellers Ranking Section */
.ranking-section {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 32px;
    overflow: hidden;
}

.ranking-section .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
}

.section-title-group {
    display: flex;
    align-items: center;
    gap: 12px;
}

.section-title-group h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.refresh-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: #f3f4f6;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
    background: #e5e7eb;
}

.refresh-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.refresh-btn i {
    font-size: 20px;
    color: #6b7280;
}

.refresh-btn i.spinning {
    animation: spin 1s linear infinite;
}

.ranking-table-wrapper {
    overflow-x: auto;
}

.ranking-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 900px;
}

.ranking-table th,
.ranking-table td {
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
}

.ranking-table th {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    background: #f9fafb;
}

.ranking-row {
    cursor: pointer;
    transition: background-color 0.2s;
}

.ranking-row:hover {
    background-color: #f9fafb;
}

.rank-col {
    width: 60px;
    text-align: center;
}

.rank-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #f3f4f6;
    font-weight: 700;
    font-size: 14px;
    color: #6b7280;
}

.rank-badge.gold {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    color: white;
}

.rank-badge.gold i {
    font-size: 18px;
}

.rank-badge.silver {
    background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
    color: white;
}

.rank-badge.silver i {
    font-size: 18px;
}

.rank-badge.bronze {
    background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
    color: white;
}

.rank-badge.bronze i {
    font-size: 18px;
}

.seller-col {
    min-width: 200px;
}

.seller-cell {
    display: flex;
    align-items: center;
    gap: 12px;
}

.seller-avatar-small {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 14px;
    flex-shrink: 0;
}

.seller-details {
    display: flex;
    flex-direction: column;
}

.seller-details .seller-name {
    font-weight: 600;
    color: #111827;
    font-size: 14px;
}

.seller-details .seller-company {
    font-size: 12px;
    color: #6b7280;
}

.revenue-col {
    width: 140px;
}

.revenue-value {
    font-weight: 700;
    color: #059669;
    font-size: 14px;
}

.orders-col {
    width: 100px;
    text-align: center;
}

.orders-value {
    font-weight: 600;
    color: #111827;
    background: #ecfdf5;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 13px;
}

.rate-col {
    width: 130px;
}

.rate-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.rate-bar {
    flex: 1;
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    overflow: hidden;
}

.rate-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.3s ease;
}

.rate-fill.confirmation {
    background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
}

.rate-fill.delivery {
    background: linear-gradient(90deg, #10b981 0%, #059669 100%);
}

.rate-fill.global {
    background: linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%);
}

.rate-value {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    min-width: 36px;
    text-align: right;
}

.evolution-col {
    width: 100px;
    text-align: center;
}

.evolution-indicators {
    display: flex;
    justify-content: center;
}

.evolution-badge {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
}

.evolution-badge i {
    font-size: 14px;
}

.evolution-badge.positive {
    background: #ecfdf5;
    color: #059669;
}

.evolution-badge.negative {
    background: #fef2f2;
    color: #dc2626;
}

.evolution-badge.neutral {
    background: #f3f4f6;
    color: #6b7280;
}

@media (max-width: 768px) {
    .am-dashboard {
        padding: 16px;
    }

    .dashboard-header {
        flex-direction: column;
    }

    .date-filter {
        width: 100%;
    }

    .dashboard-sections {
        grid-template-columns: 1fr;
    }

    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .custom-dates {
        flex-direction: column;
    }

    .ranking-section .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .section-title-group {
        flex-wrap: wrap;
    }
}
</style>
